<!--
  Eigenständiges, unsichtbares Custom Element - lauscht auf ein globales Event und öffnet dann
  unser bestehendes DetailOverlay, unabhängig davon, welche/wie viele der anderen Widgets
  (Spielplan/Startseite/Story) gerade sonst auf der Seite sind. Für Stellen wie die Tipp-Cards,
  die selbst kein eigenes unserer Widgets sind (reines Host-Seiten-HTML/JS), aber trotzdem unser
  reichhaltiges Overlay (Aufstellung/Events/Statistik/Tabelle/Story) für ein Match öffnen sollen.

  Nutzung von der Host-Seite aus (reines JS, kein Vue nötig):
    window.dispatchEvent(new CustomEvent('tm-open-match-detail', {
      detail: { matchId: 123, tab: 'vorschau' } // tab optional - fehlt es, wird automatisch
      // das sinnvollste VERFÜGBARE Tab für dieses Match gewählt (siehe ermittleStandardTab)
    }))

  WICHTIG: Es darf nur EINE Instanz dieses Custom Elements pro Seite geben - sonst würden bei
  einem einzigen Event mehrere Overlays gleichzeitig aufgehen. Deshalb bewusst als eigenes,
  separates Element statt in eines der bestehenden Widgets eingebaut (die können ja mehrfach auf
  derselben Seite vorkommen).
-->
<template>
  <div class="tm-overlay-host">
    <Teleport :to="portalZiel">
      <Transition name="tm-sheet-fade">
        <div v-if="offenesDetail || ladeMatchLaeuft" class="tm-sheet-backdrop" @click="schliesseDetail"></div>
      </Transition>
      <Transition name="tm-sheet-slide">
        <div v-if="ladeMatchLaeuft" class="tm-detail-overlay tm-detail-overlay--schmal">
          <div class="tm-state"><span class="tm-state__spinner"></span> Lade Match...</div>
        </div>
        <div v-else-if="ladeMatchFehler" class="tm-detail-overlay tm-detail-overlay--schmal">
          <div class="tm-state tm-state--error tm-state--column">
            {{ ladeMatchFehler }}
            <button type="button" class="tm-retry-btn" @click="schliesseDetail">Schließen</button>
          </div>
        </div>
        <DetailOverlay
          v-else-if="offenesDetail"
          :detail="offenesDetail"
          :api-base="props.apiBase"
          @close="schliesseDetail"
          @change-tab="wechsleTab"
        />
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { getPortalTarget } from './lib/portal.js'
import { ladeEinzelnesMatch } from './lib/matchLookup.js'
import DetailOverlay from './components/DetailOverlay.vue'
import { wendeStoriesAnMatchAn } from './lib/storyAnMatch.js'

const props = defineProps({
  apiBase: { type: String, default: '/rest/courseservice/oddsservice/free' },
  flaggenBasisUrl: { type: String, default: 'https://app.team-match.io' },
  // Der Name des window-Events, auf das gelauscht wird - konfigurierbar, falls der Host-Seite
  // später mal ein anderer Name lieber wäre, ohne dass wir hier fest verdrahten müssen.
  eventName: { type: String, default: 'tm-open-match-detail' }
})

const portalZiel = getPortalTarget()

const offenesDetail = ref(null) // { match, tab }
const ladeMatchLaeuft = ref(false)
const ladeMatchFehler = ref(null)

// NEU: gleiche Priorisierungs-Logik wie StartseiteWidget.vue's bestimmeTab() - statt blind
// 'vorschau' anzunehmen (das könnte ja für dieses Match gar nicht existieren, dann sähe man nur
// "Noch keine Vorschau verfügbar" statt tatsächlich verfügbarer Inhalte), wird das sinnvollste
// TATSÄCHLICH VERFÜGBARE Tab gewählt: Spielbericht (falls Spiel vorbei + Bericht da), sonst
// Vorschau (falls vorhanden), sonst Aufstellung/Events/Statistik als Fallback.
function ermittleStandardTab(match) {
  // GEÄNDERT: hatLineup/hatEvents/hatStatistik verlassen sich bisher nur auf die vom Server
  // separat gelieferten Flags - können falsch/veraltet sein (beobachtet: events-Array mit
  // Einträgen, hatEvents trotzdem false). Zusätzlich die tatsächlichen Arrays prüfen.
  const hatLineup = !!match.hatLineup || (Array.isArray(match.lineups) && match.lineups.length > 0)
  const hatEvents = !!match.hatEvents || (Array.isArray(match.events) && match.events.length > 0)
  const hatStatistik = !!match.hatStatistik || (Array.isArray(match.statistiken) && match.statistiken.length > 0)

  const zeigeSpielbericht = match.status === 'beendet' && !!match.storyReport
  if (zeigeSpielbericht) return 'spielbericht'
  if (match.storyPreview) return 'vorschau'
  if (hatLineup) return 'aufstellung'
  if (hatEvents) return 'events'
  if (hatStatistik) return 'statistik'
  // Tabelle als Fallback - jede Liga mit bekannter ligaId hat potenziell eine.
  if (match.ligaId != null) return 'tabelle'
  return 'vorschau' // Fallback, kommt praktisch nie vor (keins der obigen Felder vorhanden)
}

async function aufEventReagieren(event) {
  const { matchId, tab } = event.detail ?? {}
  if (matchId == null) {
    console.warn(`[OverlayHost] Event "${props.eventName}" ohne matchId im detail ignoriert`)
    return
  }

  ladeMatchLaeuft.value = true
  ladeMatchFehler.value = null
  offenesDetail.value = null

  try {
    const match = await ladeEinzelnesMatch(props.apiBase, matchId, props.flaggenBasisUrl)

    try {
      const res = await fetch(`${props.apiBase}/widget/get-stories-matches?matchIds=${encodeURIComponent(match.id)}`)
      if (res.ok) wendeStoriesAnMatchAn(match, await res.json(), props.bildBasisUrl)
    } catch (e) {
      console.warn('[OverlayHost] Stories konnten nicht geladen werden:', e)
    }

    offenesDetail.value = { match, tab: tab ?? ermittleStandardTab(match) }
  } catch (e) {
    ladeMatchFehler.value = 'Match konnte nicht geladen werden.'
    console.warn('[OverlayHost] ladeEinzelnesMatch fehlgeschlagen:', e)
  } finally {
    ladeMatchLaeuft.value = false
  }
}

function schliesseDetail() {
  offenesDetail.value = null
  ladeMatchFehler.value = null
}

function wechsleTab(tab) {
  if (offenesDetail.value) offenesDetail.value = { ...offenesDetail.value, tab }
}

onMounted(() => {
  window.addEventListener(props.eventName, aufEventReagieren)
})
onUnmounted(() => {
  window.removeEventListener(props.eventName, aufEventReagieren)
})
</script>